import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

interface Payment {
  id: string;
  user: {
    id: string;
    full_name: string;
    email: string;
  };
  subscription: {
    id: string;
    plan: {
      name: string;
    };
  };
  amount_vatu: number;
  payment_status: string;
  payment_method: string;
  payment_reference: string;
  payment_proof_url: string;
  submitted_at: string;
  admin_notes: string;
}

const PaymentApproval = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, [filter]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('payment_transactions')
        .select(`
          id,
          amount_vatu,
          payment_status,
          payment_method,
          payment_reference,
          payment_proof_url,
          submitted_at,
          admin_notes,
          user_profiles!user_id (
            id,
            full_name,
            email
          ),
          user_subscriptions!subscription_id (
            id,
            subscription_plans!plan_id (
              plan_name
            )
          )
        `)
        .eq('payment_status', filter)
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match the existing interface structure
      const transformedData = data?.map((payment: any) => ({
        ...payment,
        user: payment.user_profiles,
        subscription: {
          ...payment.user_subscriptions,
          plan: {
            name: payment.user_subscriptions?.subscription_plans?.plan_name
          }
        }
      }));

      setPayments(transformedData || []);
    } catch (err: any) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (paymentId: string) => {
    try {
      setProcessing(true);
      const { error } = await supabase
        .from('payment_transactions')
        .update({
          payment_status: 'approved',
          approved_at: new Date().toISOString(),
          admin_notes: adminNotes
        })
        .eq('id', paymentId);

      if (error) throw error;

      // Generate & email invoice to the payer (group admin) after approval.
      // Note: This requires the Edge Function `send-payment-invoice` to be deployed.
      try {
        const { data, error: fnError } = await supabase.functions.invoke('send-payment-invoice', {
          body: { paymentId },
        });
        if (fnError) throw fnError;
        if (!data?.ok) {
          console.warn('Invoice generation/email returned non-ok:', data);
        }
      } catch (invoiceErr) {
        console.error('Invoice generation/email failed:', invoiceErr);
        alert('Payment approved, but invoice email failed. Please check Edge Function logs.');
      }

      setShowModal(false);
      setSelectedPayment(null);
      setAdminNotes('');
      await fetchPayments();
    } catch (err: any) {
      console.error('Error approving payment:', err);
      alert('Failed to approve payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (paymentId: string) => {
    if (!adminNotes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      setProcessing(true);
      const { error } = await supabase
        .from('payment_transactions')
        .update({
          payment_status: 'rejected',
          rejection_reason: adminNotes,
          admin_notes: adminNotes
        })
        .eq('id', paymentId);

      if (error) throw error;

      setShowModal(false);
      setSelectedPayment(null);
      setAdminNotes('');
      await fetchPayments();
    } catch (err: any) {
      console.error('Error rejecting payment:', err);
      alert('Failed to reject payment');
    } finally {
      setProcessing(false);
    }
  };

  const openModal = (payment: Payment) => {
    setSelectedPayment(payment);
    setAdminNotes(payment.admin_notes || '');
    setShowModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-success bg-success/10';
      case 'rejected': return 'text-destructive bg-destructive/10';
      case 'pending': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">Payment Approvals</h2>
            <div className="flex gap-2">
              {(['pending', 'approved', 'rejected'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-3 py-1 rounded-sm text-sm font-medium transition-interactive ${
                    filter === status
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-muted/30">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {payment.user?.full_name || 'Unknown User'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {payment.user?.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground">
                    {payment.subscription?.plan?.name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-foreground">
                    {payment.amount_vatu} VT
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {payment.payment_reference || 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(payment.payment_status)}`}>
                      {payment.payment_status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => openModal(payment)}
                      className="text-primary hover:text-primary/80 text-sm font-medium transition-interactive"
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {payments.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No {filter} payments found
            </div>
          )}
        </div>
      </div>

      {/* Payment Review Modal */}
      {showModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
          <div className="bg-card rounded-lg shadow-elevation-2 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-foreground">Payment Review</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-muted-foreground hover:text-foreground transition-interactive"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Payment Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">User</label>
                  <p className="text-foreground">{selectedPayment.user?.full_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedPayment.user?.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Plan</label>
                  <p className="text-foreground">{selectedPayment.subscription?.plan?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Amount</label>
                  <p className="text-foreground font-semibold">{selectedPayment.amount_vatu} VT</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Payment Method</label>
                  <p className="text-foreground">{selectedPayment.payment_method}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Reference</label>
                  <p className="text-foreground">{selectedPayment.payment_reference || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Submitted</label>
                  <p className="text-foreground">
                    {new Date(selectedPayment.submitted_at).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Payment Proof */}
              {selectedPayment.payment_proof_url && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground block mb-2">
                    Payment Proof
                  </label>
                  <a
                    href={selectedPayment.payment_proof_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 text-sm underline"
                  >
                    View Payment Proof
                  </a>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <label className="text-sm font-medium text-muted-foreground block mb-2">
                  Admin Notes {selectedPayment.payment_status === 'pending' && <span className="text-destructive">*</span>}
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={
                    selectedPayment.payment_status === 'pending' ?'Add notes for approval/rejection' :'View existing notes'
                  }
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground min-h-[100px]"
                  disabled={selectedPayment.payment_status !== 'pending'}
                />
              </div>
            </div>

            {/* Actions */}
            {selectedPayment.payment_status === 'pending' && (
              <div className="p-6 border-t border-border flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-sm border border-border text-foreground hover:bg-muted transition-interactive"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleReject(selectedPayment.id)}
                  className="px-4 py-2 rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-interactive"
                  disabled={processing}
                >
                  {processing ? 'Processing...' : 'Reject'}
                </button>
                <button
                  onClick={() => handleApprove(selectedPayment.id)}
                  className="px-4 py-2 rounded-sm bg-success text-white hover:bg-success/90 transition-interactive"
                  disabled={processing}
                >
                  {processing ? 'Processing...' : 'Approve'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentApproval;